import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContactsRequest } from '../../../store/slices/contactsSlice';
import { CONTACTS_PAGE_SIZE } from '../data/contactsData';
import { buildListContactsPayload } from '../utils/contactsApiMapper';

function buildFetchPayload({ search, filterParams, page, pageKeys }) {
  return buildListContactsPayload({
    search,
    limit: CONTACTS_PAGE_SIZE,
    ...filterParams,
    ...(page > 1 && pageKeys[page] ? { lastEvaluatedKey: pageKeys[page] } : {}),
  });
}

export function useContactsPagination({ appliedSearch, filterParams, hasWhatsApp }) {
  const dispatch = useDispatch();
  const { loading, loaded, lastEvaluatedKey, lastListParams } = useSelector(
    (state) => state.contacts
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageKeys, setPageKeys] = useState({ 1: null });

  const filterParamsKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);

  const hasNextPage = Boolean(lastEvaluatedKey);
  const totalPages = hasNextPage ? currentPage + 1 : currentPage;

  const availablePages = useMemo(() => {
    const pages = Object.keys(pageKeys).map(Number);
    if (hasNextPage && !pages.includes(currentPage + 1)) {
      pages.push(currentPage + 1);
    }
    return pages.sort((a, b) => a - b);
  }, [pageKeys, hasNextPage, currentPage]);

  const fetchPage = useCallback(
    (page, keys = pageKeys) => {
      dispatch(
        fetchContactsRequest(
          buildFetchPayload({
            search: appliedSearch,
            filterParams,
            page,
            pageKeys: keys,
          })
        )
      );
      setCurrentPage(page);
    },
    [appliedSearch, dispatch, filterParams, pageKeys]
  );

  const resetAndFetchFirstPage = useCallback(
    (search = appliedSearch) => {
      setCurrentPage(1);
      setPageKeys({ 1: null });
      dispatch(
        fetchContactsRequest(
          buildListContactsPayload({
            search,
            limit: CONTACTS_PAGE_SIZE,
            ...filterParams,
          })
        )
      );
    },
    [appliedSearch, dispatch, filterParams]
  );

  useEffect(() => {
    if (!hasWhatsApp) return;
    setCurrentPage(1);
    setPageKeys({ 1: null });
    dispatch(
      fetchContactsRequest(
        buildListContactsPayload({
          search: appliedSearch,
          limit: CONTACTS_PAGE_SIZE,
          ...filterParams,
        })
      )
    );
  }, [hasWhatsApp, appliedSearch, filterParamsKey, dispatch, filterParams]);

  useEffect(() => {
    if (!loaded || loading || !lastEvaluatedKey) return;
    setPageKeys((prev) => ({ ...prev, [currentPage + 1]: lastEvaluatedKey }));
  }, [loaded, loading, lastEvaluatedKey, currentPage]);

  useEffect(() => {
    if (!loaded || lastListParams.lastEvaluatedKey || currentPage === 1) return;
    setCurrentPage(1);
    setPageKeys({ 1: null });
  }, [lastListParams, loaded, currentPage]);

  const handlePageChange = useCallback(
    (page) => {
      if (page < 1) return;

      if (pageKeys[page] !== undefined || page === 1) {
        fetchPage(page);
        return;
      }

      if (page === currentPage + 1 && hasNextPage) {
        fetchPage(page);
      }
    },
    [currentPage, fetchPage, hasNextPage, pageKeys]
  );

  const handlePrev = useCallback(() => {
    if (currentPage <= 1) return;
    fetchPage(currentPage - 1);
  }, [currentPage, fetchPage]);

  const handleNext = useCallback(() => {
    if (!hasNextPage) return;
    fetchPage(currentPage + 1);
  }, [currentPage, fetchPage, hasNextPage]);

  const retryFetch = useCallback(() => {
    fetchPage(currentPage);
  }, [currentPage, fetchPage]);

  const showPagination = loaded && (currentPage > 1 || hasNextPage);

  return {
    currentPage,
    totalPages,
    availablePages,
    hasNextPage,
    showPagination,
    handlePageChange,
    handlePrev,
    handleNext,
    retryFetch,
    resetAndFetchFirstPage,
  };
}
